import React, { useEffect, useState, useRef } from 'react';

import { DataGrid } from '@mui/x-data-grid';
import { Card } from '@sunwisesoftware/sunwise-ui';
import { debounce, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CustomPagination from 'common/components/CustomPagination';
import { DEBOUNCE_TIME_500, TABLE_PAGE_SIZE_OPTIONS } from 'common/constants';
import CustomColumns from 'common/modules/customColumns';
import { getOrderedColumns, getHiddenColumns } from 'common/utils/helpers';

import { COLUMN_KEYS, REQUIRED_COLUMNS_KEYS } from '../constants';

import BulkOperationsCard from './BulkOperationsCard';
import Columns from './Columns';
import Filters from './Filters';
import ProductsModal from './ProductsModal';

const ListItems = ({
    assignProduct,
    canModify,
    downloadFile,
    executives,
    filterData,
    filterItems,
    financierProducts,
    handleClickUnlink,
    isFetching,
    isFetchingExecutives,
    items,
    paginationData,
    setFilterData,
    statusList,
    tableSettings,
}) => {
    const { t } = useTranslation();
    const [rowCount, setRowCount] = useState(0);
    const [selectionModel, setSelectionModel] = useState([]);
    const [allianceId, setAllianceId] = useState(null);
    const [currentFilterData, setCurrentFilterData] = useState(filterData);

    useEffect(() => {
        if (paginationData)
            setRowCount((prevRowCount) =>
                paginationData?.total_rows !== undefined
                    ? paginationData?.total_rows
                    : prevRowCount,
            );
    }, [paginationData?.total_rows, setRowCount]);

    useEffect(() => {
        if (!isEqual(currentFilterData, filterData)) {
            handleOnFilter(filterData);
            setCurrentFilterData(filterData);
        }
    }, [filterData]);

    const handleOnFilter = useRef(
        debounce((filter) => filterItems(filter), DEBOUNCE_TIME_500),
    ).current;

    const handleOnChangeFilter = (value, key) =>
        setFilterData((prev) => ({ ...prev, [key]: value }));

    const handleOnChangeSearch = (event) => {
        setFilterData((prev) => ({
            ...prev,
            page: 0,
            searchText: event.target.value,
        }));
    };

    const callback = () => {
        setAllianceId(null);
        setSelectionModel([]);
    };

    const handleClickAssignProduct = (alliancesIds) => {
        setAllianceId(alliancesIds[0]);
        assignProduct(alliancesIds, callback);
    };

    const pageSizeOptions = TABLE_PAGE_SIZE_OPTIONS;
    const columns = Columns({
        canModify,
        handleClickAssignProduct,
        handleClickUnlink,
        hiddenColumns: getHiddenColumns(tableSettings),
        orders: getOrderedColumns(tableSettings),
        selectionModel,
    });

    const columnVisibilityModel = columns.reduce(
        (acc, column) => ({ ...acc, [column.field]: !column.hide }),
        {},
    );

    const getRowHeight = () => 'auto';
    const initialState = {
        pagination: {
            paginationModel: { pageSize: TABLE_PAGE_SIZE_OPTIONS[0] },
        },
    };

    const onPaginationModelChange = (model) =>
        setFilterData((prev) => ({ ...prev, ...model }));
    const onRowSelectionModelChange = (model) => setSelectionModel(model);
    const onSortModelChange = (sortModel) => {
        if (!sortModel[0]) return;
        setFilterData((prev) => ({
            ...prev,
            orderBy: sortModel[0].field.toLocaleLowerCase(),
            sortBy: sortModel[0].sort,
        }));
    };

    const slots = { pagination: CustomPagination };
    const slotProps = {
        pagination: {
            customContent: (
                <CustomColumns.Container
                    columnKeys={COLUMN_KEYS}
                    requiredColumnsKeys={REQUIRED_COLUMNS_KEYS}
                    tableKey="installers"
                />
            ),
            pageSizeOptions,
        },
    };

    const prefix = t('{{count}} selected alliance', {
        count: selectionModel.length,
    });

    return (
        <>
            <Filters
                downloadFile={downloadFile}
                executives={executives}
                filterData={filterData}
                financierProducts={financierProducts}
                handleOnChangeFilter={handleOnChangeFilter}
                handleOnChangeSearch={handleOnChangeSearch}
                isFetchingExecutives={isFetchingExecutives}
                statusList={statusList}
            />

            <Card>
                <Card.Body>
                    <BulkOperationsCard
                        canModify={canModify}
                        handleClickAssignProduct={assignProduct}
                        prefix={prefix}
                        selectionModel={selectionModel}
                        setSelectionModel={setSelectionModel}
                    />
                    <DataGrid
                        checkboxSelection={true}
                        columnVisibilityModel={columnVisibilityModel}
                        columns={columns}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        editMode="row"
                        getRowHeight={getRowHeight}
                        initialState={initialState}
                        loading={isFetching}
                        onPaginationModelChange={onPaginationModelChange}
                        onRowSelectionModelChange={onRowSelectionModelChange}
                        onSortModelChange={onSortModelChange}
                        pageSizeOptions={pageSizeOptions}
                        pagination
                        paginationMode="server"
                        rowCount={rowCount}
                        rowSelectionModel={selectionModel}
                        rows={items}
                        slotProps={slotProps}
                        slots={slots}
                        sortingMode="server"
                    />
                </Card.Body>
            </Card>

            <ProductsModal
                allianceId={allianceId}
                alliancesFilterData={filterData}
            />
        </>
    );
};

ListItems.propTypes = {
    assignProduct: PropTypes.func,
    canModify: PropTypes.bool,
    downloadFile: PropTypes.func,
    executives: PropTypes.array,
    filterData: PropTypes.object,
    filterItems: PropTypes.func,
    financierProducts: PropTypes.array,
    handleClickUnlink: PropTypes.func,
    isFetching: PropTypes.bool,
    isFetchingExecutives: PropTypes.bool,
    items: PropTypes.array,
    paginationData: PropTypes.object,
    setFilterData: PropTypes.func,
    statusList: PropTypes.array,
    tableSettings: PropTypes.array,
};

export default ListItems;
